import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, type ReactNode } from 'react';
import { config } from '@/app/config';
import { useAppStore } from '@/app/store';
import { persistSupabaseSession } from '@/repositories';
import { getSupabase } from '@/supabase/client';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

export function AppProviders({ children }: { children: ReactNode }) {
  const bootstrap = useAppStore((s) => s.bootstrap);
  const setOnline = useAppStore((s) => s.setOnline);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  useEffect(() => {
    if (config.mode === 'demo') return;
    const supabase = getSupabase();
    if (!supabase) return;

    void supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session) return;
      void persistSupabaseSession(session).then((authSession) => {
        useAppStore.setState({ session: authSession });
        void useAppStore.getState().refreshSpaceData();
      });
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) {
        void (async () => {
          const { unsubscribeSpaceRealtime } = await import('@/sync/supabaseSync');
          await unsubscribeSpaceRealtime();
          useAppStore.setState({
            session: null,
            profiles: [],
            space: null,
            invite: null,
            plans: [],
            monthFocus: [],
            preferences: null,
            spaceReady: false,
          });
        })();
        return;
      }
      void persistSupabaseSession(session).then((authSession) => {
        useAppStore.setState({ session: authSession });
        void useAppStore.getState().refreshSpaceData();
      });
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
    };
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    };
  }, [setOnline]);

  // Advance deferred sync retries so "retrying" cannot hang until a manual click.
  useEffect(() => {
    if (config.mode !== 'production') return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const schedule = async () => {
      if (cancelled) return;
      try {
        const { listSyncQueue, isOpDue } = await import('@/sync/engine');
        const queue = await listSyncQueue();
        if (queue.length === 0) return;

        const now = Date.now();
        const hasDue = queue.some((op) => isOpDue(op, now, false));
        const nextAt = queue
          .map((op) => (op.nextRetryAt ? new Date(op.nextRetryAt).getTime() : now))
          .filter((t) => Number.isFinite(t))
          .sort((a, b) => a - b)[0];

        if (hasDue) {
          const { requestSyncNow } = await import('@/sync/supabaseSync');
          await requestSyncNow({ force: false });
          await useAppStore.getState().refreshSyncStatus();
        }

        if (cancelled) return;
        const wait = Math.max(1_000, Math.min((nextAt ?? now + 15_000) - Date.now(), 60_000));
        timer = setTimeout(() => void schedule(), hasDue ? wait : Math.max(wait, 2_000));
      } catch {
        timer = setTimeout(() => void schedule(), 15_000);
      }
    };

    void schedule();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
